import { Link } from "react-router-dom";
import type { Film } from "@/types/film";
import { getRecommendations, typeLabels } from "@/lib/films";

interface DetailSidebarProps {
  film: Film;
}

export default function DetailSidebar({ film }: DetailSidebarProps) {
  const recommendations = getRecommendations(film, 4);
  const info = [
    { label: "类型", value: typeLabels[film.type] },
    { label: "地区", value: film.region },
    { label: "上映年份", value: String(film.year) },
    { label: "片长", value: film.length },
    { label: "导演", value: film.director },
    { label: "评分", value: `${film.rating.toFixed(1)} / 10` },
  ];

  return (
    <aside className="space-y-5">
      <section className="rounded-lg border border-background-200 bg-background-50 p-5">
        <h2 className="text-sm font-bold text-foreground-950">作品信息</h2>
        <dl className="mt-4 space-y-2.5">
          {info.map((item) => (
            <div key={item.label} className="flex items-start justify-between gap-4 text-[13px]">
              <dt className="shrink-0 text-foreground-500">{item.label}</dt>
              <dd className="text-right text-foreground-900">{item.value}</dd>
            </div>
          ))}
        </dl>
        <div className="mt-4 border-t border-background-200 pt-4">
          <p className="label-caps text-[10px] text-foreground-500">Keywords</p>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {film.tags.map((tag) => (
              <span
                key={tag}
                className="rounded bg-secondary-100 px-2 py-0.5 text-[11px] text-secondary-900"
              >
                {tag}
              </span>
            ))}
          </div>
        </div>
      </section>

      <section className="rounded-lg border border-background-200 bg-background-50 p-5">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-bold text-foreground-950">相关推荐</h2>
          <span className="text-[10px] text-foreground-500">同类型 / 同演员</span>
        </div>
        <ul className="mt-4 space-y-3.5">
          {recommendations.map((item) => (
            <li key={item.id}>
              <Link
                to={`/detail/${item.id}`}
                className="group flex items-center gap-3 cursor-pointer"
              >
                <span className="h-[72px] w-[48px] shrink-0 overflow-hidden rounded-md bg-secondary-900">
                  <img
                    src={item.poster}
                    alt={`${item.title} 海报`}
                    title={`${item.title} 相关推荐`}
                    className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[13px] font-medium text-foreground-900 transition-colors group-hover:text-primary-600">
                    {item.title}
                  </span>
                  <span className="mt-1 block truncate text-[11px] text-foreground-500">
                    {item.year} · {item.region}
                  </span>
                  <span className="mt-1 flex items-center gap-1 text-[11px] text-accent-600">
                    <i className="ri-star-fill text-[10px]" />
                    {item.rating.toFixed(1)}
                  </span>
                </span>
              </Link>
            </li>
          ))}
        </ul>
        <Link
          to={`/category/${film.type}`}
          className="mt-4 flex items-center justify-between border-t border-background-200 pt-3 text-xs font-medium text-primary-700 transition-colors hover:text-accent-600 cursor-pointer"
        >
          浏览更多{typeLabels[film.type]}
          <i className="ri-arrow-right-line" />
        </Link>
      </section>

      <section className="rounded-lg bg-primary-900 p-5 text-background-100">
        <span className="label-caps text-[10px] text-accent-300">Reading</span>
        <h2 className="mt-2 text-sm font-bold text-background-50">想看更多深度解读？</h2>
        <p className="mt-2 text-[12px] leading-6 text-background-200/80">
          专题页按主题聚合影评，从视点、方法到余味，逐层拆解一部作品。
        </p>
        <Link
          to="/special/silent-current"
          className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-accent-300 transition-colors hover:text-accent-200 cursor-pointer"
        >
          进入影评专题
          <i className="ri-arrow-right-line" />
        </Link>
      </section>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 px-1 text-xs text-foreground-500">
        <Link to="/" className="transition-colors hover:text-primary-600 cursor-pointer">
          <i className="ri-home-4-line mr-1" />
          返回首页
        </Link>
        <Link
          to={`/category/${film.type}`}
          className="transition-colors hover:text-primary-600 cursor-pointer"
        >
          <i className="ri-arrow-left-line mr-1" />
          返回{typeLabels[film.type]}列表
        </Link>
      </div>
    </aside>
  );
}